import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Spinner, Text } from '@fluent/ui';
import { mintCardUpdateTransaction } from './manage.js';
import { closeCheckout, onPaddleEvent, openInlineCheckout } from './paddle.js';
import { trackMetaCheckout } from '../lib/metaPixel.js';
import { formatDateLong, toDateLang } from '../lib/dates.js';
import './PaymentFrame.css';

/**
 * PaymentFrame — Paddle's PCI card iframe inside our own chrome.
 *
 * One component for every place fluent.ge takes a card: /subscription and
 * the lesson paywall (Tornike 2026-09-21: the paywall used Paddle's own
 * pop-up, the subscription page used this — he wanted this in both).
 */

export type FrameMode = 'subscribe' | 'resubscribe' | 'update' | 'fix';

export interface PaymentFrameProps {
  mode: FrameMode;
  email: string;
  userId: string;
  /** Trial or period end from the subscription row, when there is one. */
  trialOrPeriodEnd: string | null;
  onClose: () => void;
  onCompleted: () => void;
}

/** CSS class Paddle mounts the checkout iframe into (frameTarget). */
const FRAME_TARGET = 'fluent-paddle-slot';

/** The Paddle price carries a 7-day trial (DECISIONS 2026-04-16). */
const TRIAL_DAYS = 7;

export function PaymentFrame({ mode, email, userId, trialOrPeriodEnd, onClose, onCompleted }: PaymentFrameProps) {
  const { t, i18n } = useTranslation();
  const [frameState, setFrameState] = useState<'loading' | 'ready' | 'error'>('loading');
  const attemptRef = useRef(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let disposed = false;
    setFrameState('loading');

    const off = onPaddleEvent((e) => {
      if (disposed) return;
      if (e.name === 'checkout.loaded') setFrameState('ready');
      if (e.name === 'checkout.error') setFrameState('error');
      if (e.name === 'checkout.completed') {
        // Only a new subscription is an ads conversion; a card swap is not.
        if (mode === 'subscribe' || mode === 'resubscribe') trackMetaCheckout(e.data);
        onCompleted();
      }
    });

    // 8s ceiling for the iframe to come up before we offer a retry.
    const timeoutId = window.setTimeout(() => {
      if (!disposed) {
        setFrameState((s) => (s === 'loading' ? 'error' : s));
      }
    }, 8000);

    (async () => {
      try {
        if (mode === 'update' || mode === 'fix') {
          const txnId = await mintCardUpdateTransaction();
          if (disposed) return;
          await openInlineCheckout({ frameTarget: FRAME_TARGET, userId, email, transactionId: txnId });
        } else {
          await openInlineCheckout({ frameTarget: FRAME_TARGET, userId, email, returning: mode === 'resubscribe' });
        }
        // The open may have resolved AFTER unmount cleanup already ran —
        // close the orphan so a ghost checkout session can't linger.
        if (disposed) void closeCheckout();
      } catch {
        if (!disposed) setFrameState('error');
      }
    })();

    return () => {
      disposed = true;
      off();
      window.clearTimeout(timeoutId);
      void closeCheckout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only on mode/attempt; identities of the rest are stable per mount
  }, [mode, attempt]);

  // A new subscriber has no row yet, so no trial end to show — it is
  // TRIAL_DAYS from today. (This used to render "first charge — —".)
  const firstCharge =
    trialOrPeriodEnd ?? new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const summary =
    mode === 'subscribe'
      ? t('sub_page.frame.summary_trial', {
          date: formatDateLong(firstCharge, toDateLang(i18n.language)),
        })
      : mode === 'resubscribe'
        ? t('sub_page.frame.summary_resub')
        : mode === 'update'
          ? t('sub_page.frame.summary_update')
          : t('sub_page.frame.summary_fix');

  return (
    <section className="fluent-payframe">
      <div className="fluent-payframe__head">
        <Heading level={4} as="h2">
          {t('sub_page.frame.title')}
        </Heading>
        <Button variant="ghost" size="md" onClick={onClose}>
          {t('sub_page.frame.close')}
        </Button>
      </div>
      <div className="fluent-payframe__summary">
        <span className="fluent-payframe__summary-label">{summary}</span>
      </div>
      <div className="fluent-payframe__slotwrap" data-state={frameState}>
        {/* Paddle mounts its iframe here (frameTarget class). */}
        <div className={FRAME_TARGET} />
        {frameState === 'loading' && (
          <div className="fluent-payframe__overlay" role="status">
            <Spinner size={22} />
            <Text size="sm" className="fluent-payframe__overlay-text">
              {t('sub_page.frame.loading')}
            </Text>
          </div>
        )}
        {frameState === 'error' && (
          <div className="fluent-payframe__overlay" role="alert">
            <Text size="sm" className="fluent-payframe__overlay-text">
              {t('sub_page.frame.error')}
            </Text>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                attemptRef.current += 1;
                setAttempt(attemptRef.current);
              }}
            >
              {t('sub_page.frame.retry')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
